import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from './supabaseClient';
import './Auth.css';

const REDIRECT_URL = `${window.location.origin}/portal`;

const AuthPage = () => {
  const [activeForm, setActiveForm] = useState(null); // null | 'signin' | 'signup'
  const [signInEmail, setSignInEmail] = useState('');
  const [signInPassword, setSignInPassword] = useState('');
  const [signUpEmail, setSignUpEmail] = useState('');
  const [signUpPassword, setSignUpPassword] = useState('');
  const [signInMsg, setSignInMsg] = useState('');
  const [signUpMsg, setSignUpMsg] = useState('');
  const [signUpIsError, setSignUpIsError] = useState(false);
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [loadingSignIn, setLoadingSignIn] = useState(false);
  const [loadingSignUp, setLoadingSignUp] = useState(false);

  const handleGoogle = async () => {
    setLoadingGoogle(true);
    await supabase.auth.signInWithOAuth({ provider: 'google', options: { redirectTo: REDIRECT_URL } });
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setSignInMsg('');
    setLoadingSignIn(true);
    const { error } = await supabase.auth.signInWithPassword({ email: signInEmail, password: signInPassword });
    setLoadingSignIn(false);
    if (error) setSignInMsg(error.message);
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setSignUpMsg('');
    setLoadingSignUp(true);
    const { error } = await supabase.auth.signUp({ email: signUpEmail, password: signUpPassword, options: { emailRedirectTo: REDIRECT_URL } });
    setLoadingSignUp(false);
    if (error) { setSignUpIsError(true); setSignUpMsg(error.message); }
    else { setSignUpIsError(false); setSignUpMsg('Check your email to confirm your account, then sign in.'); }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    if (!signInEmail) { setSignInMsg('Enter your email above first.'); return; }
    await supabase.auth.resetPasswordForEmail(signInEmail, { redirectTo: REDIRECT_URL });
    setSignInMsg('Password reset email sent — check your inbox.');
  };

  const toggle = (form) => setActiveForm(prev => prev === form ? null : form);

  return (
    <div className="auth-container">
      <div className="auth-panel auth-panel-brand">
        <div className="auth-brand-mark">ServSolo</div>
        <div className="auth-brand-copy">
          <div className="auth-eyebrow">Digital storefronts for modern operators</div>
          <h1>Menus, kiosks, previews, and store control in one place.</h1>
          <p>Launch branded ordering experiences, manage multiple locations, and keep admin, restaurant, and customer journeys under one roof.</p>
        </div>
        <div className="auth-brand-points">
          <div>Live menu previews</div>
          <div>Multi-store management</div>
          <div>Invite-based team access</div>
        </div>
      </div>

      <div className="auth-panel auth-box">
        <Link className="auth-back-link" to="/">← Back to site</Link>

        <div className="auth-box-header">
          <div className="auth-box-eyebrow">Welcome</div>
          <h1>Get started</h1>
          <p>Sign in or create a new account below.</p>
        </div>

        <button className="auth-google-btn" onClick={handleGoogle} disabled={loadingGoogle}>
          <svg width="18" height="18" viewBox="0 0 18 18"><path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/><path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/><path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/><path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z"/></svg>
          {loadingGoogle ? 'Redirecting…' : 'Continue with Google'}
        </button>

        <div className="auth-or"><span>OR</span></div>

        <button
          className={`auth-toggle-btn${activeForm === 'signin' ? ' active' : ''}`}
          onClick={() => toggle('signin')}
        >
          <span>Sign in via email</span>
          <span className="auth-toggle-arrow">{activeForm === 'signin' ? '▲' : '▼'}</span>
        </button>

        {activeForm === 'signin' && (
          <form onSubmit={handleSignIn} className="auth-form">
            <input className="auth-input" type="email" placeholder="Email address" value={signInEmail} onChange={e => setSignInEmail(e.target.value)} required />
            <input className="auth-input" type="password" placeholder="Password" value={signInPassword} onChange={e => setSignInPassword(e.target.value)} required />
            {signInMsg && <div className="auth-msg">{signInMsg}</div>}
            <button className="auth-btn-primary" type="submit" disabled={loadingSignIn}>{loadingSignIn ? 'Signing in…' : 'Sign In'}</button>
            <a className="auth-forgot" href="#forgot" onClick={handleForgot}>Forgot password?</a>
          </form>
        )}

        <div className="auth-or"><span>OR</span></div>

        <button
          className={`auth-toggle-btn${activeForm === 'signup' ? ' active' : ''}`}
          onClick={() => toggle('signup')}
        >
          <span>Create an account</span>
          <span className="auth-toggle-arrow">{activeForm === 'signup' ? '▲' : '▼'}</span>
        </button>

        {activeForm === 'signup' && (
          <form onSubmit={handleSignUp} className="auth-form">
            <input className="auth-input" type="email" placeholder="Email address" value={signUpEmail} onChange={e => setSignUpEmail(e.target.value)} required />
            <input className="auth-input" type="password" placeholder="Create a password (min. 6 characters)" value={signUpPassword} onChange={e => setSignUpPassword(e.target.value)} required minLength={6} />
            {signUpMsg && <div className={signUpIsError ? 'auth-msg auth-msg-error' : 'auth-msg auth-msg-success'}>{signUpMsg}</div>}
            <button className="auth-btn-primary" type="submit" disabled={loadingSignUp}>{loadingSignUp ? 'Creating…' : 'Create Account'}</button>
          </form>
        )}
      </div>
    </div>
  );
};

export default AuthPage;
